import { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { admin, filterBtn, inputCls } from "./adminApi";

const numToField = (n) => (n === null || n === undefined ? "" : String(n));
const toInt = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function InventoryRow({ row, draft, setDraft, onSave }) {
  const currentStock = numToField(row.stock);
  const currentBatch = numToField(row.batch_size);
  const stockVal = draft.stock !== undefined ? draft.stock : currentStock;
  const batchVal = draft.batch_size !== undefined ? draft.batch_size : currentBatch;
  const dirty = stockVal !== currentStock || batchVal !== currentBatch;
  return (
    <div data-testid={`inv-row-${row.variant_id}`} className="flex flex-wrap md:flex-nowrap items-center gap-3 md:gap-4 p-4">
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm">{row.product_name}<span className="text-[color:var(--bl-gold)] ml-2">· {row.strength}</span></div>
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mt-1 flex flex-wrap gap-2 items-center">
          <span>{row.sku} · A${row.price.toFixed(2)}</span>
          {row.sold_out && <span className="text-red-300 border border-red-400/40 px-2 py-0.5">Sold Out</span>}
          {row.low && !row.sold_out && <span className="text-[color:var(--bl-gold)] border border-[color:var(--bl-gold)]/40 px-2 py-0.5">Low · {row.stock}</span>}
          {row.batch_size ? (
            <span className="text-[color:var(--bl-blue)] border border-[color:var(--bl-blue)]/40 px-2 py-0.5">Batch · {row.stock ?? 0}/{row.batch_size}</span>
          ) : null}
          {row.stock === null && row.batch_size == null && <span className="text-white/40 border border-white/15 px-2 py-0.5">Untracked</span>}
        </div>
      </div>
      <input type="number" min="0" data-testid={`inv-input-${row.variant_id}`} value={stockVal} placeholder="—"
        onChange={(e) => setDraft({ stock: e.target.value })} className={`w-20 ${inputCls} px-2 py-2 text-xs text-right`} />
      <input type="number" min="0" data-testid={`inv-batch-${row.variant_id}`} value={batchVal} placeholder="—"
        onChange={(e) => setDraft({ batch_size: e.target.value })} className={`w-24 ${inputCls} px-2 py-2 text-xs text-right`} />
      <button onClick={() => onSave(stockVal, batchVal)} disabled={!dirty} data-testid={`inv-save-${row.variant_id}`}
        className="bl-btn gold text-[10px] py-2 px-3 disabled:opacity-30 disabled:cursor-not-allowed shrink-0">Save</button>
    </div>
  );
}

export default function AdminInventory({ onUnauthed, onSaved }) {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await admin.get(`/admin/inventory`);
      setInventory(r.data.items);
      setDrafts({});
    } catch (err) {
      if (err?.response?.status === 401) onUnauthed();
    } finally { setLoading(false); }
  }, [onUnauthed]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    if (filter === "low") return inventory.filter(i => i.low || i.sold_out);
    if (filter === "tracked") return inventory.filter(i => i.stock !== null);
    return inventory;
  }, [inventory, filter]);

  const save = async (variantId, stockVal, batchVal) => {
    const stock = toInt(stockVal), batch = toInt(batchVal);
    if (stock !== null && (Number.isNaN(stock) || stock < 0)) { toast.error("Stock must be a non-negative number"); return; }
    if (batch !== null && (Number.isNaN(batch) || batch < 0)) { toast.error("Batch size must be a non-negative number"); return; }
    try {
      await admin.put(`/admin/inventory/${variantId}`, { stock, batch_size: batch });
      toast.success(stock === null && batch === null ? "Untracked" : "Saved");
      await Promise.all([load(), onSaved()]);
    } catch { toast.error("Failed to save"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-inventory-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5">
          {[["all", `All (${inventory.length})`], ["low", "Low / Sold Out"], ["tracked", "Tracked"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`inventory-filter-${k}`} className={filterBtn(filter === k)}>{label}</button>
          ))}
        </div>
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">Stock · units left &nbsp;·&nbsp; Batch · original run size</div>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="animate-spin text-white/40 mx-auto" /></div>
      ) : visible.length === 0 ? (
        <div className="text-white/40 py-8 text-center">Nothing to show</div>
      ) : (
        <div className="border border-white/8 divide-y divide-white/8">
          <div className="hidden md:flex items-center gap-4 px-4 py-3 bg-white/[0.02] font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">
            <div className="flex-1">Variant</div><div className="w-20 text-right">Stock</div><div className="w-24 text-right">Batch</div><div className="w-16"></div>
          </div>
          {visible.map((row) => (
            <InventoryRow key={row.variant_id} row={row} draft={drafts[row.variant_id] || {}}
              setDraft={(patch) => setDrafts(d => ({ ...d, [row.variant_id]: { ...d[row.variant_id], ...patch } }))}
              onSave={(s, b) => save(row.variant_id, s, b)} />
          ))}
        </div>
      )}
    </div>
  );
}
